import React, { useEffect, useRef, useState } from 'react';
import {
  AppBar, Toolbar, Box, Button, Avatar, Typography,
  Popover, Divider, useTheme, useMediaQuery, Modal,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import SearchBox from '../../../components/SearchBox';
import LanguageSelector from '../../../components/LanguageSelector';
import SignInCard from '../../../components/auth/SignInCard';
import Auth from '../../../common/auth/auth';
import { Evt, EvtNameSpace, EvtType } from '../../../common/util/event-manager';
import { DefaultService } from '../../../_genApi/scs';
import { AUTH_TOKEN_KEY, AUTH_USER_KEY } from '../../../common/auth/auth-api';
import { getDeviceId } from '../../../common/device-id';
import { useLocale } from '../../../hooks/useLocale';

type UserInfo = {
  uid?: number;
  username?: string;
  fullName?: string;
  avatar?: string;
};

export default function StoryCraftHeader() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = theme.palette.mode === 'dark';
  const { s } = useLocale();

  const isHeroPage = location.pathname === '/dashboards/all-games';
  const [isAtTop, setIsAtTop] = useState(true);

  const [searchText, setSearchText] = useState('');
  const [user, setUser] = useState<UserInfo | undefined>(undefined);
  const [avatarPopoverOpen, setAvatarPopoverOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  const loadUser = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) { setUser(undefined); return; }
    try {
      const userInfo = await DefaultService.postApiMe({ deviceId: getDeviceId() });
      setUser(userInfo as UserInfo);
    } catch {
      try {
        const cached = localStorage.getItem(AUTH_USER_KEY);
        if (cached) setUser(JSON.parse(cached) as UserInfo);
      } catch {}
    }
  };

  useEffect(() => {
    if (!isHeroPage) { setIsAtTop(false); return; }
    setIsAtTop(window.scrollY < 80);
    const onScroll = () => setIsAtTop(window.scrollY < 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHeroPage]);

  useEffect(() => {
    const onLoggedIn = () => loadUser();
    const onLoggedOut = () => setUser(undefined);
    const onOpenLoginModal = () => setShowLoginModal(true);
    Evt.on(EvtNameSpace.Auth, EvtType.LoggedIn, onLoggedIn);
    Evt.on(EvtNameSpace.Auth, EvtType.LoggedOut, onLoggedOut);
    Evt.on(EvtNameSpace.UI, EvtType.OpenLoginModal, onOpenLoginModal);
    if (localStorage.getItem(AUTH_TOKEN_KEY)) loadUser();
    return () => {
      Evt.off(EvtNameSpace.Auth, EvtType.LoggedIn, onLoggedIn);
      Evt.off(EvtNameSpace.Auth, EvtType.LoggedOut, onLoggedOut);
      Evt.off(EvtNameSpace.UI, EvtType.OpenLoginModal, onOpenLoginModal);
    };
  }, []);

  const handleSearch = () => {
    if (searchText.trim()) {
      navigate(`/dashboards/search?title=${encodeURIComponent(searchText.trim())}`);
    }
  };

  const handleSignOut = () => {
    setAvatarPopoverOpen(false);
    Auth.logout();
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{ background: 'transparent', zIndex: 1100 }}
      >
        {/* Fading background overlay */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
          background: isDark ? 'rgba(19,21,31,0.88)' : 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(91,77,255,0.1)',
          boxShadow: '0 2px 8px rgba(91,77,255,0.06)',
          opacity: isAtTop ? 0 : 1,
          transition: 'opacity 0.2s ease',
        }} />
        <Toolbar sx={{ position: 'relative', zIndex: 1, height: 64, minHeight: '64px !important', px: 0, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', maxWidth: '1200px', mx: 'auto', px: { xs: 2, sm: 3, md: 5 } }}>

          {/* Logo */}
          <Box
            component={RouterLink}
            to="/dashboards/all-games"
            sx={{
              display: isAtTop ? 'none' : 'flex',
              alignItems: 'center', gap: 1, textDecoration: 'none', flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={process.env.REACT_APP_PUBLIC_URL_SHORT + '/favicon.ico'}
              alt="StoryCraft"
              sx={{ width: 36, height: 36, borderRadius: '8px' }}
            />
            {!isMobile && (
              <Box
                component="img"
                src={process.env.REACT_APP_PUBLIC_URL_SHORT + '/bcs-title.png'}
                alt="StoryCraft"
                sx={{ height: 20, width: 'auto', objectFit: 'contain' }}
              />
            )}
          </Box>

          {/* Search */}
          {!isMobile && !isAtTop && (
            <Box sx={{ flex: 1, maxWidth: 480 }}>
              <SearchBox
                onSearchTextChanged={(text) => setSearchText(text)}
                startSearch={() => handleSearch()}
                placeholder={s.search_placeholder}
              />
            </Box>
          )}

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Create CTA */}
          <Button
            variant="contained"
            onClick={() => window.open(window.location.origin + '/create/index.html', '_blank')}
            sx={{
              background: isAtTop
                ? 'rgba(255,255,255,0.18)'
                : 'linear-gradient(135deg, #5B4DFF, #7C6BFF)',
              backdropFilter: isAtTop ? 'blur(4px)' : 'none',
              border: isAtTop ? '1px solid rgba(255,255,255,0.4)' : 'none',
              color: '#fff',
              fontWeight: 700,
              borderRadius: '10px',
              px: { xs: 1.5, sm: 2.5 },
              fontSize: { xs: '13px', sm: '14px' },
              whiteSpace: 'nowrap',
              textTransform: 'none',
              boxShadow: isAtTop ? 'none' : '0 4px 12px rgba(91,77,255,0.35)',
              transition: 'background 0.3s, box-shadow 0.3s, border 0.3s',
              '&:hover': {
                background: isAtTop
                  ? 'rgba(255,255,255,0.28)'
                  : 'linear-gradient(135deg, #4A3EEA, #6B5AEE)',
                boxShadow: isAtTop ? 'none' : '0 6px 16px rgba(91,77,255,0.45)',
              },
            }}
          >
            {isMobile ? '+' : s.header_btn_create}
          </Button>

          {/* Language selector */}
          {!isMobile && (
            <Box sx={isAtTop ? {
              '& > div > div:first-of-type': {
                backgroundColor: 'rgba(255,255,255,0.18) !important',
                border: '1px solid rgba(255,255,255,0.4) !important',
                backdropFilter: 'blur(4px)',
              },
              '& svg': { color: 'rgba(255,255,255,0.8) !important' },
              '& p, & span': { color: '#fff !important' },
            } : {}}>
              <LanguageSelector isMobile={true} />
            </Box>
          )}

          {/* Auth Avatar */}
          <Box ref={avatarRef}>
            {user ? (
              <Avatar
                src={user.avatar}
                sx={{ width: 36, height: 36, cursor: 'pointer', border: isAtTop ? '2px solid rgba(255,255,255,0.7)' : '2px solid #5B4DFF' }}
                onClick={() => navigate('/management/profile/details')}
              />
            ) : (
              <Avatar
                sx={{
                  width: 36, height: 36, cursor: 'pointer',
                  backgroundColor: isAtTop ? 'rgba(255,255,255,0.18)' : '#E8E5FF',
                  color: isAtTop ? '#fff' : '#5B4DFF',
                  border: isAtTop ? '1px solid rgba(255,255,255,0.4)' : 'none',
                  backdropFilter: isAtTop ? 'blur(4px)' : 'none',
                }}
                onClick={() => setShowLoginModal(true)}
              >
                👤
              </Avatar>
            )}
          </Box>

          </Box>
        </Toolbar>
      </AppBar>

      {/* Avatar Popover */}
      <Popover
        open={avatarPopoverOpen}
        anchorEl={avatarRef.current}
        onClose={() => setAvatarPopoverOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ minWidth: 240 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar src={user?.avatar} sx={{ width: 48, height: 48 }}>
              {(user?.fullName || user?.username || 'U')[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" fontWeight={700} noWrap>
                {user?.fullName || user?.username || s.header_user_default}
              </Typography>
              {user?.username && user?.fullName && user.username !== user.fullName && (
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  @{user.username}
                </Typography>
              )}
            </Box>
          </Box>
          <Divider />
          <Button
            fullWidth
            size="small"
            sx={{ justifyContent: 'flex-start', px: 2, py: 1, borderRadius: 0 }}
            onClick={() => {
              setAvatarPopoverOpen(false);
              navigate('/management/profile/details');
            }}
          >
            My Profile
          </Button>
          <Divider />
          <Box sx={{ p: 1 }}>
            <Button fullWidth size="small" color="error" onClick={handleSignOut}>
              {s.header_sign_out}
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Login Modal */}
      <Modal open={showLoginModal} onClose={() => setShowLoginModal(false)}>
        <Box sx={{ outline: 'none', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: { xs: '90%', sm: 'auto' }, maxWidth: '400px' }}>
          <SignInCard onLoggedIn={() => { setShowLoginModal(false); loadUser(); }} onClose={() => setShowLoginModal(false)} />
        </Box>
      </Modal>
    </>
  );
}
